import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Spinner, Receipt, FileText, FileDoc, Users, HandCoins, UsersThree, Compass, CaretRight, IdentificationBadge, Buildings, Coins } from "@phosphor-icons/react";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { buildMenuIndex, filterMenuHits } from "../lib/menuSearch";

const ENTITY_META = {
    client:        { label: "Clients",         Icon: Users,               shortLabel: "clients"        },
    invoice:       { label: "Invoices",        Icon: Receipt,             shortLabel: "invoices"       },
    po:            { label: "Purchase Orders", Icon: FileText,            shortLabel: "POs"            },
    proposal:      { label: "Proposals",       Icon: FileDoc,             shortLabel: "proposals"      },
    payment:       { label: "Payments",        Icon: HandCoins,           shortLabel: "payments"       },
    lead:          { label: "Leads",           Icon: UsersThree,          shortLabel: "leads"          },
    // UAT 2026-07-27 defect #2 — Administrative masters surfaced in Ctrl+K.
    work_role:     { label: "Work Roles",      Icon: IdentificationBadge, shortLabel: "work roles"     },
    business_unit: { label: "Business Units",  Icon: Buildings,           shortLabel: "business units" },
    cost_centre:   { label: "Cost Centres",    Icon: Coins,               shortLabel: "cost centres"   },
};

// Mirror of backend ENTITY_PERMS in /app/backend/routes_search.py. Kept
// client-side ONLY for placeholder + scope-hint filtering (so the UX
// is truthful to the caller's RBAC). The backend remains the authority:
// `/billing/search` independently filters results, and unauthorised
// scope queries silently return [] — the FE map being stale would only
// degrade UX, never expose data. Keep the two in sync when adding a
// new entity.
const ENTITY_PERMS = {
    client:        ["read"],
    invoice:       ["finance:read"],
    payment:       ["finance:read"],
    po:            ["sales:read"],
    proposal:      ["sales:read"],
    lead:          ["sales:read"],
    work_role:     ["work_role_master:read"],
    business_unit: ["bu_master:read"],
    cost_centre:   ["cost_centre_master:read"],
};

// Prefix aliases for scoped queries. Users type "inv: 0428" or "client: acme"
// to scope the search to a single entity. Matches Linear / GitHub style.
const SCOPE_PREFIXES = {
    inv: "invoice",
    invoice: "invoice",
    client: "client",
    clients: "client",
    po: "po",
    proposal: "proposal",
    prop: "proposal",
    payment: "payment",
    pay: "payment",
    lead: "lead",
    leads: "lead",
};

const SCOPE_RE = /^([a-zA-Z]+)\s*:\s*(.*)$/;

/**
 * Parse a raw query string for a scope prefix.
 * Returns { scope, text } where scope is an entity name (or null) and text is
 * the user's actual search term (prefix stripped). Example:
 *   "inv: 0428"     -> { scope: "invoice", text: "0428" }
 *   "client:acme"   -> { scope: "client",  text: "acme" }
 *   "acme"          -> { scope: null,     text: "acme" }
 */
const parseScopedQuery = (raw) => {
    const m = raw.match(SCOPE_RE);
    if (!m) return { scope: null, text: raw };
    const prefix = m[1].toLowerCase();
    const entity = SCOPE_PREFIXES[prefix];
    if (!entity) return { scope: null, text: raw };
    return { scope: entity, text: m[2] };
};

const groupHits = (hits) => {
    const grouped = {};
    for (const h of hits) {
        if (!grouped[h.entity]) grouped[h.entity] = [];
        grouped[h.entity].push(h);
    }
    return grouped;
};

/**
 * Global search bar — sits in the AppLayout top header.
 * Click or press Cmd/Ctrl+K to open. Type → debounced backend hit.
 *
 * Placeholder text, scope-prefix hints and trigger-button label are all
 * filtered by the caller's effective permissions so they only see
 * entity hints they can actually search. The backend `/billing/search`
 * endpoint independently enforces the same gate (defence in depth) —
 * the FE filtering is a UX truthfulness affordance, not the security
 * boundary.
 */
export const GlobalSearchBar = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [hits, setHits] = useState([]);
    const [loading, setLoading] = useState(false);
    const { user, permissions, can } = useAuth();
    const navigate = useNavigate();
    const debounceRef = useRef(null);

    // Iter 103.0 Phase 2 — Menu index (Screens & Menus results).
    // Rebuilt only when the auth surface changes (user id or the
    // permission set). RBAC filtering happens inside buildMenuIndex()
    // via isItemVisible() — same gate as the sidebar. Zero new
    // authorization logic (REEDOS decision #2, Iter 103.0).
    const menuIndex = useMemo(
        () => buildMenuIndex(user, can),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user?.id, permissions],
    );

    // Menu hits update synchronously per keystroke — no network hop,
    // no debounce. Record hits still debounced (see the search effect
    // further down). Screens & Menus group is rendered ABOVE the
    // record groups (REEDOS decision #3, Iter 103.0).
    const menuHits = useMemo(
        () => filterMenuHits(menuIndex, query, 8),
        [menuIndex, query],
    );

    // Compute the set of entities the caller may search. `permissions`
    // is a Set<string> exposed by AuthContext, sourced from `/auth/me`
    // which itself uses role_master.resolve_permissions (DB-authoritative
    // post Authorization Source-of-Truth Migration). Memoised so the
    // derived data does not recompute on every keystroke.
    const {
        accessibleEntities,
        accessibleScopeHints,
        placeholder,
        triggerLabel,
    } = useMemo(() => {
        const accessible = Object.entries(ENTITY_PERMS)
            .filter(([, needed]) => needed.some((p) => permissions?.has?.(p)))
            .map(([e]) => e);
        const hintList = accessible.map((e) => ({
            prefix: `${e === "invoice" ? "inv" : e}:`,
            label: `${ENTITY_META[e].label} only`,
            entity: e,
        }));
        const shortList = accessible.map((e) => ENTITY_META[e].shortLabel);
        const prefixList = accessible.map(
            (e) => `${e === "invoice" ? "inv" : e}:`,
        );
        const placeholderText = accessible.length === 0
            ? "Search…"
            : `Search — or use ${prefixList.join(", ")}`;
        const triggerText = accessible.length === 0
            ? "Search…"
            : `Search ${shortList.slice(0, 3).join(", ")}${shortList.length > 3 ? "…" : "…"}`;
        return {
            accessibleEntities: new Set(accessible),
            accessibleScopeHints: hintList,
            placeholder: placeholderText,
            triggerLabel: triggerText,
        };
    }, [permissions]);

    // Cmd+K / Ctrl+K to open
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((o) => !o);
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    // Reset state on open/close
    useEffect(() => {
        if (!open) {
            setQuery("");
            setHits([]);
        }
    }, [open]);

    // Debounced search — parses scope prefixes like "inv:" / "client:"
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const { scope, text } = parseScopedQuery(query);
        const clean = (text || "").trim();
        if (!clean) {
            setHits([]);
            setLoading(false);
            return;
        }
        // Permission-aware short-circuit: scope explicitly excluded by
        // RBAC → return empty without round-tripping. Mirrors the
        // backend's silent-drop posture and saves the hit.
        if (scope && !accessibleEntities.has(scope)) {
            setHits([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const params = { q: clean };
                if (scope) params.entity = scope;
                const { data } = await api.get(`/billing/search`, { params });
                // Defence in depth — drop any hit whose entity the
                // caller isn't permitted to see. Backend already does
                // this; this catches drift between the two ENTITY_PERMS
                // maps without exposing unauthorised entities.
                const safe = Array.isArray(data)
                    ? data.filter((h) => accessibleEntities.has(h.entity))
                    : [];
                setHits(safe);
            } catch {
                setHits([]);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [query, accessibleEntities]);

    const onPick = (href) => {
        setOpen(false);
        navigate(href);
    };

    const grouped = groupHits(hits);
    const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC");
    const shortcut = isMac ? "⌘K" : "Ctrl+K";
    const { scope: activeScope, text: activeText } = parseScopedQuery(query);
    const cleanQuery = (activeText || "").trim();
    const scopeLabel = activeScope ? ENTITY_META[activeScope]?.label : null;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="global-search-trigger"
                className="group inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors w-72 max-w-full text-sm whitespace-nowrap overflow-hidden"
            >
                <MagnifyingGlass size={14} weight="bold" className="shrink-0" />
                <span className="flex-1 text-left truncate">{triggerLabel}</span>
                <kbd className="hidden sm:inline-flex shrink-0 items-center gap-0.5 text-[10px] font-mono text-slate-400 bg-slate-100 group-hover:bg-slate-200 px-1.5 py-0.5 rounded">
                    {shortcut}
                </kbd>
            </button>

            <CommandDialog open={open} onOpenChange={setOpen} commandProps={{ shouldFilter: false }}>
                {activeScope && (
                    <div className="flex items-center gap-2 px-3 pt-3 pb-1 border-b border-slate-100" data-testid="global-search-scope-chip">
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                            {scopeLabel}
                        </span>
                        <span className="text-xs text-slate-500">Scope filter active</span>
                    </div>
                )}
                <CommandInput
                    placeholder={placeholder}
                    value={query}
                    onValueChange={setQuery}
                    data-testid="global-search-input"
                />
                <CommandList>
                    {!cleanQuery && (
                        <div className="py-8 text-sm text-slate-500">
                            <div className="text-center mb-5">
                                <MagnifyingGlass size={26} className="mx-auto mb-2.5 text-slate-300" />
                                <p className="font-medium text-slate-700">Search across REEDOS</p>
                                {accessibleScopeHints.length > 0 ? (
                                    <p className="text-xs mt-1.5 text-slate-400">
                                        {accessibleScopeHints.map((h) => ENTITY_META[h.entity].label).join(" · ")}
                                    </p>
                                ) : (
                                    <p className="text-xs mt-1.5 text-slate-400">
                                        No searchable scopes available for your role.
                                    </p>
                                )}
                            </div>
                            {accessibleScopeHints.length > 0 && (
                                <div className="px-3">
                                    <div className="reedos-eyebrow px-2 mb-1.5">Scoped filters</div>
                                    <div className="space-y-0.5">
                                        {accessibleScopeHints.map((h) => {
                                            const Icon = ENTITY_META[h.entity].Icon;
                                            return (
                                                <button
                                                    key={h.prefix}
                                                    type="button"
                                                    onClick={() => setQuery(h.prefix + " ")}
                                                    data-testid={`global-search-scope-hint-${h.entity}`}
                                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 text-left"
                                                >
                                                    <Icon size={14} className="text-slate-500" />
                                                    <code className="text-xs font-mono text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded">{h.prefix}</code>
                                                    <span className="text-xs text-slate-500">{h.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {cleanQuery && loading && (
                        <div className="py-8 text-center text-sm text-slate-500">
                            <Spinner size={18} className="mx-auto animate-spin text-slate-400" />
                            <p className="mt-2">Searching{scopeLabel ? ` ${scopeLabel.toLowerCase()}` : ""}…</p>
                        </div>
                    )}
                    {cleanQuery && !loading && hits.length === 0 && menuHits.length === 0 && (
                        <CommandEmpty>
                            <div className="py-6 text-center" data-testid="global-search-empty">
                                <p className="text-sm font-medium text-slate-700">
                                    No matching screens or records found.
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                    Try another keyword or browse from the navigation menu.
                                </p>
                            </div>
                        </CommandEmpty>
                    )}
                    {cleanQuery && menuHits.length > 0 && (
                        <CommandGroup heading="Screens & Menus" data-testid="global-search-menu-group">
                            {menuHits.map((m) => {
                                const Icon = m.icon || Compass;
                                return (
                                    <CommandItem
                                        key={`menu-${m.id}`}
                                        value={`menu-${m.id}`}
                                        onSelect={() => onPick(m.href)}
                                        data-testid={`global-search-menu-hit-${m.id}`}
                                        className="cursor-pointer"
                                    >
                                        <Icon size={16} className="mr-2 text-indigo-500 shrink-0" weight="regular" />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium text-slate-900 flex items-center gap-1.5">
                                                {m.label}
                                                {m.hidden && (
                                                    <span className="inline-flex items-center rounded bg-slate-100 text-slate-500 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wider" data-testid={`global-search-menu-hidden-${m.id}`}>
                                                        Search-only
                                                    </span>
                                                )}
                                            </div>
                                            {m.breadcrumb && m.breadcrumb.length > 1 && (
                                                <div className="truncate text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                                                    {/* Iter 103.0 · render PARENT crumbs only —
                                                        drop the terminal label to avoid
                                                        double-printing (label is already
                                                        the primary heading above). */}
                                                    {m.breadcrumb.slice(0, -1).map((crumb, i, arr) => (
                                                        <span key={i} className="flex items-center gap-1">
                                                            <span>{crumb}</span>
                                                            {i < arr.length - 1 && (
                                                                <CaretRight size={9} className="text-slate-300" weight="bold" />
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    )}
                    {Object.entries(grouped).map(([entity, list]) => {
                        const meta = ENTITY_META[entity] || { label: entity, Icon: MagnifyingGlass };
                        const Icon = meta.Icon;
                        return (
                            <CommandGroup key={entity} heading={meta.label}>
                                {list.map((h) => (
                                    <CommandItem
                                        key={`${entity}-${h.id}`}
                                        // Disable cmdk's substring filtering — backend already filtered.
                                        value={`${h.label} ${h.hint || ""} ${h.id}`}
                                        onSelect={() => onPick(h.href)}
                                        data-testid={`global-search-hit-${entity}-${h.id}`}
                                        className="cursor-pointer"
                                    >
                                        <Icon size={16} className="mr-2 text-slate-500 shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium text-slate-900">{h.label}</div>
                                            {h.hint && <div className="truncate text-[11px] text-slate-500 mt-0.5">{h.hint}</div>}
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        );
                    })}
                </CommandList>
            </CommandDialog>
        </>
    );
};

export default GlobalSearchBar;
